/**
 * Selekt data layer - one interface over both kinds of sources:
 *   - local: items given at init (or parsed from the original <option> tree)
 *   - endpoint: an API list endpoint called through g.fetch
 *
 * The component only ever calls:
 *   source.load(search, page) -> Promise<{ items, more }>
 *   source.lookup(ids)        -> Promise<items>   (endpoint lists must accept
 *                                data.ids and return just those items)
 *   source.clearCache()       -> drop everything cached (dependsOn changes)
 *   source.add(item)          -> register a locally created item
 *
 * Endpoint calls follow the house API rules: business filters go in data
 * ({ ...params }), list control goes in pagination ({ page, perPage, search }).
 * Every response is cached per search+page+params until clearCache().
 *
 * Item shape (any depth): { value, label, disabled?, children?, extra? }.
 */

export function normalizeItems(raw) {
	return (raw || []).map((it) => ({
		value: it.value,
		label: String(it.label !== undefined ? it.label : it.value),
		disabled: !!it.disabled,
		extra: it.extra || {},
		children: it.children && it.children.length ? normalizeItems(it.children) : null,
	}));
}

// Depth-first flat view: [{ item, depth }] - used for rendering + keyboard nav.
export function flatten(items, depth = 0, out = []) {
	for (const item of items) {
		out.push({ item, depth });
		if (item.children) flatten(item.children, depth + 1, out);
	}
	return out;
}

export function findByValue(items, value) {
	for (const item of items) {
		if (String(item.value) === String(value)) return item;
		if (item.children) {
			const found = findByValue(item.children, value);
			if (found) return found;
		}
	}
	return null;
}

// All selectable descendants of an item (for selectChildren mode).
export function selectableDescendants(item, out = []) {
	for (const child of item.children || []) {
		if (!child.disabled) out.push(child);
		selectableDescendants(child, out);
	}
	return out;
}

// Tree search: a matching item keeps its whole subtree; a matching descendant
// is returned inside its chain of ancestors.
export function filterTree(items, search) {
	if (!search) return items;
	const needle = search.toLowerCase();
	const out = [];
	for (const item of items) {
		const selfMatch = item.label.toLowerCase().includes(needle);
		const kids = item.children ? filterTree(item.children, search) : null;
		if (selfMatch) out.push(item);
		else if (kids && kids.length) out.push({ ...item, children: kids });
	}
	return out;
}

export function createSource(cfg) {
	// cfg: { items?, endpoint?, getParams(), perPage }
	if (!cfg.endpoint) {
		let items = normalizeItems(cfg.items || []);
		return {
			local: true,
			async load(search) {
				return { items: filterTree(items, search), more: false };
			},
			async lookup(ids) {
				return ids.map((v) => findByValue(items, v)).filter(Boolean);
			},
			clearCache() {},
			add(item) {
				items = items.concat(normalizeItems([item]));
			},
		};
	}

	const cache = new Map();
	const cacheKey = (search, page) => JSON.stringify([search, page, cfg.getParams()]);

	async function call(data, pagination) {
		// progress: false - the control shows its own loading state; list
		// loads/lookups should not flash the app-wide top bar.
		// unguarded: the caller's generation counter is the guard (a click opens the list)
		const res = await window.g.fetch(cfg.endpoint, data, { pagination, progress: false, unguarded: true });
		if (!res.success) throw new Error(res.error.message);
		if (res.error && res.error.code) throw new Error(res.error.message);
		return res;
	}

	return {
		local: false,
		async load(search, page) {
			const key = cacheKey(search, page);
			if (cache.has(key)) return cache.get(key);
			const res = await call({ ...cfg.getParams() }, { page, perPage: cfg.perPage, search });
			const out = { items: normalizeItems(res.data || []), more: !!(res.pagination && res.pagination.more) };
			cache.set(key, out);
			return out;
		},
		async lookup(ids) {
			try {
				const res = await call({ ids, ...cfg.getParams() }, { page: 1, perPage: ids.length || 1 });
				return normalizeItems(res.data || []);
			} catch (err) {
				return [];
			}
		},
		clearCache() {
			cache.clear();
		},
		add() {}, // created items come back from the server on the next load
	};
}
